








































































































































import CopyToClipboardButton from '@/app/components/CopyToClipboardButton.vue';
import { useFeatureFlags, useFilters } from '@/app/composable';
import store from '@/app/store';
import { ArchiveIcon, CloudDownloadIcon, LinkIcon, PencilAltIcon, ReplyIcon, TrashIcon, EyeIcon } from '@vue-hero-icons/outline';
import { CalendarIcon, UserIcon } from '@vue-hero-icons/solid';
import { PropType, Ref, computed, defineComponent } from '@vue/composition-api';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import utc from 'dayjs/plugin/utc';
import * as R from 'ramda';
import { useAssetRetrieval } from '../composable/asset-retrieval';
import { useAssetStatus } from '../composable/asset-status';
import { AssetType, AssetTypeId, StatusCode } from '../constants';
import { Asset } from '../types';
import CardHydro from '@/app/components/CardHydro.vue';

dayjs.extend(utc);
dayjs.extend(minMax);

export default defineComponent({
    name: 'AssetOverview',
    components: {
        CardHydro,
        TrashIcon,
        PencilAltIcon,
        LinkIcon,
        ArchiveIcon,
        ReplyIcon,
        UserIcon,
        CalendarIcon,
        CloudDownloadIcon,
        CopyToClipboardButton,
        EyeIcon
    },
    props: {
        asset: {
            type: Object as PropType<Asset>,
            required: true,
        },
        filter: {
            type: String,
            required: true,
        },
        alert: {
            type: Object as PropType<{ entityId: string | number; alertId: string; enabled: boolean }>,
            required: false,
        },
        contract: {
            type: Object as PropType<{ id: string; offlineRetention: string[] }>,
            default: null,
        },
        acquiredAssetIds: {
            type: Array as PropType<number[]>,
            default: () => [],
        },
    },
    setup(props, { emit, root }) {
        const { flag } = useFeatureFlags();
        const isAlertsEnabled = flag('alerts');

        const user = computed(() => store.state.auth.user);
        const isAssetCreator = computed(() => user.value && user.value.id === props.asset.createdBy.id);
        const model = computed(() => props.asset && props.asset.assetTypeId === AssetTypeId.Model);

        const updatedDate = computed(() =>
            props.asset.modifiedAt
                ? dayjs.max([dayjs(props.asset.modifiedAt), dayjs(props.asset.updatedAt)]).toString()
                : props.asset.updatedAt,
        );

        const { formatDateTime } = useFilters();

        const assetType = computed(() => {
            switch (props.asset.assetTypeId) {
                case AssetTypeId.Model:
                    return {
                        type: AssetType.Model,
                        class: 'bg-purple-100 text-purple-600',
                    };
                case AssetTypeId.Result:
                    return {
                        type: AssetType.Result,
                        class: 'bg-indigo-100 text-indigo-600',
                    };
                default:
                    return {
                        type: AssetType.Dataset,
                        class: 'bg-orange-100 text-orange-600',
                    };
            }
        });

        const allFilterEnabled = computed(() => props.filter === 'all');

        const status = computed(() => props.asset?.status);
        const contract = computed(() => props.contract);
        const acquiredAssetIds = computed(() => props.acquiredAssetIds);
        const asset: Ref<Asset> = computed<Asset>(() => props.asset);
        const { label: assetStatusLabel, colour: statusClass } = useAssetStatus(status);
        const {
            isRetrievalDisabled,
            retrievalTooltip,
            accessibilityKeys,
            belongsToUserOrOrganisation,
            canRetrieve,
            isAcquiredAndNotOther,
        } = useAssetRetrieval(asset, user, contract, acquiredAssetIds);
        const deletedProvenance = computed(() => R.pathOr(false, ['metadata', 'provenance', 'deleted'], props.asset));
        const navigateToProvenance = () =>
            deletedProvenance.value || assetUploadedByUser.value ? null : emit('navigate', props.asset);
        const assetUploadedByUser = computed(() => !R.pathOr(null, ['metadata', 'provenance'], props.asset));

        const provenanceLinkTooltip = computed(() => {
            if (assetUploadedByUser.value) {
                return 'The asset was uploaded by the User';
            }

            const pipelineType = props.asset.assetTypeId === AssetTypeId.Dataset ? 'Data Collection' : 'Analytics';

            if (deletedProvenance.value)
                return `The ${pipelineType} Pipeline, which created this asset, has been deleted.`;
            else return `Navigate to ${pipelineType} Job`;
        });

        const retrieve = () => {
            root.$router.push({
                name: 'retrieval:create',
                params: {
                    assetId: props.asset.id,
                    accessibility: accessibilityKeys.value,
                    contractId: contract.value?.id,
                    acquired: isAcquiredAndNotOther.value,
                } as any,
            });
        };

        return {
            dayjs,
            emit,
            statusClass,
            isAlertsEnabled,
            assetStatusLabel,
            user,
            model,
            StatusCode,
            status,
            isAssetCreator,
            allFilterEnabled,
            AssetType,
            assetType,
            deletedProvenance,
            provenanceLinkTooltip,
            navigateToProvenance,
            R,
            retrieve,
            retrievalTooltip,
            isRetrievalDisabled,
            belongsToUserOrOrganisation,
            formatDateTime,
            updatedDate,
            canRetrieve,
        };
    },
});
